
import React, { useState } from 'react';
import { Box, Chip, MenuItem, Typography } from '@mui/material';
import { CustomSelect, TextField, CustomRadioGroup, CustomCheckbox, DatePicker } from '../../../v2/MUI_custom_component';
import CustomChip from '../../Components/CustomChip';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';


const FieldInput = ({ fieldType, options = [], value, onChange, label, questionTitle }) => {
  const [tagInput, setTagInput] = useState('');

  // tags — free-form chip input for array values
  if (fieldType === 'tags') {
    const tags = Array.isArray(value) ? value : [];
    const handleAdd = () => {
      const trimmed = tagInput.trim();
      if (trimmed && !tags.includes(trimmed)) {
        onChange([...tags, trimmed]);
      }
      setTagInput('');
    };
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {tags .length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {tags.map((tag, idx) => (
              <CustomChip
                key={idx}
                label={tag}
                selected={true}
                onDelete={() => onChange(tags.filter((_, i) => i !== idx))}
              />
            ))}
          </Box>
        )}
        <TextField
          // label={'Brands'}
          variant="outlined"
          size="small"
          fullWidth
          placeholder="Type and press Enter"
          value={tagInput}
          onChange={e => setTagInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
        />
      </Box>
    );
  }

  // radio — single-select using CustomRadioGroup
  if (fieldType === 'radio') {
    return (
      <CustomRadioGroup
        options={options}
        value={value || ''}
        size='small'
        onChange={e => onChange(e.target.value)}
      />
    );
  }

  // checkbox — multi-select using CustomCheckbox per option
  if (fieldType === 'checkbox') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {options.map(opt => {
          const checked = Array.isArray(value) && value.includes(opt.value);
          return (
            <CustomCheckbox
              size='small'
              key={opt.value}
              label={opt.label}
              checked={checked}
              onChange={() => {
                const current = Array.isArray(value) ? value : [];
                onChange(checked ? current.filter(v => v !== opt.value) : [...current, opt.value]);
              }}
            />
          );
        })}
      </Box>
    );
  }

  // select — dropdown
  if (fieldType === 'select') {
    return (
    <CustomSelect
        fullWidth
        size="small"
        value={value || ''}
        onChange={e => onChange(e.target.value)}
    >
        {options.map(opt => (
          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
        ))}
      </CustomSelect>
    );
  }

  // date — date picker
  if (fieldType === 'date') {
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          value={value ? dayjs(value) : null}
          onChange={(newValue) => {
            const dateString = newValue ? newValue.format('YYYY-MM-DD') : '';
            onChange(dateString);
          }}
          disableFuture
          format="DD/MM/YYYY"
          slotProps={{ textField: { fullWidth: true, size: 'small' } }}
        />
      </LocalizationProvider>
    );
  }

  // number — numeric input
  if (fieldType === 'number') {
    return (
      <TextField
        type="number"
        variant="outlined"
        fullWidth
        size='small'
        value={value ?? ''}
        onChange={e => onChange(e.target.value === '' ? '' : Number(e.target.value))}
        inputProps={{ min: 0 }}
      />
    );
  }

  // textarea — multi-line
  if (fieldType === 'textarea') {
    return (
      <TextField
        variant="outlined"
        fullWidth
        multiline
        size='small'
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        sx={{
          '& .MuiInputBase-root': {
            alignItems: 'flex-start',
          },
          '& .MuiInputBase-input': {
            maxHeight: 80,
            overflow: 'auto',
          }
        }}
      />
    );
  }
  // preferred_language — chips for preferred, dropdown for others, selected as chips (first)
  if (fieldType === 'preferred_language') {
    const opts = options && typeof options === 'object' ? options : {};
    const preferredOptions = Array.isArray(opts.preferred) ? opts.preferred : [];
    const otherOptions = Array.isArray(opts.others)
      ? opts.others
      : Array.isArray(options)
        ? options
        : [];
    const selected = Array.isArray(value) ? value : [];
    const handleSelect = (lang) => {
      if (!selected.includes(lang)) {
        onChange([...selected, lang]);
      }
    };
    const handleRemove = (lang) => {
      onChange(selected.filter(l => l !== lang));
    };
    const unselectedPreferred = preferredOptions.filter(opt => !selected.includes(opt.value));
    const availableOthers = otherOptions.filter(opt => !selected.includes(opt.value));
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, overflowX: 'auto', flexWrap: 'nowrap', py: 1 }}>
          {selected.map((lang, idx) => (
            <CustomChip
              key={lang}
              label={lang}
              selected={true}
              onDelete={() => handleRemove(lang)}
            />
          ))}
          {unselectedPreferred.map(opt => (
            <CustomChip
              key={opt.value}
              label={opt.label}
              selected={false}
              onClick={() => handleSelect(opt.value)}
            />
          ))}
        </Box>
        {availableOthers.length > 0 && (
          <CustomSelect
            fullWidth
            size="small"
            value={''}
            displayEmpty
            onChange={e => handleSelect(e.target.value)}
            placeholder={'Choose additional languages'}
          >
            {availableOthers.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </CustomSelect>
        )}
      </Box>
    );
  }

  // text (default) — single-line
  return (
    <TextField
        variant="outlined"
        fullWidth
        size='small'
        value={value || ''}
        onChange={e => onChange(e.target.value)}
    />
  );
};

export default FieldInput;
